import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/services/db/supabase';
import { useAuthStore } from '@/store/authStore';

const PAGE_SIZE = 25;

type SWASeverityFilter = 'all' | 'minor' | 'moderate' | 'serious' | 'critical';
const SWA_SEVERITY_VALUES: ReadonlyArray<SWASeverityFilter> = [
  'all',
  'minor',
  'moderate',
  'serious',
  'critical',
];

interface Filters {
  dateFrom: string;
  dateTo: string;
  status: string;
  projectId: string;
  risk: 'all' | 'low' | 'medium' | 'high' | 'critical';
  swaSeverity: SWASeverityFilter;
}

const defaultFilters = (projectId?: string | null): Filters => ({
  dateFrom: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  dateTo: format(new Date(), 'yyyy-MM-dd'),
  status: 'all',
  // SSRs are scoped to a single project — pin the filter so the table
  // never shows other projects' reports even if the SSR clears filters.
  projectId: projectId ?? 'all',
  risk: 'all',
  swaSeverity: 'all',
});

// Drill-downs from the dashboards (S82b/S82c) navigate with ?project=,
// ?risk=, ?status=, ?swa_severity= query params. Honour them on initial
// mount and when the params change.
function applyUrlOverrides(
  base: Filters,
  params: URLSearchParams,
): Filters {
  const next = { ...base };
  const project = params.get('project');
  if (project) next.projectId = project;
  const risk = params.get('risk');
  if (risk === 'low' || risk === 'medium' || risk === 'high' || risk === 'critical') {
    next.risk = risk;
  }
  const status = params.get('status');
  if (status && ['draft', 'submitted', 'reviewed', 'flagged', 'amended'].includes(status)) {
    next.status = status;
  }
  const swa = params.get('swa_severity');
  if (swa && SWA_SEVERITY_VALUES.includes(swa as SWASeverityFilter)) {
    next.swaSeverity = swa as SWASeverityFilter;
  }
  return next;
}

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  draft: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Draft' },
  submitted: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Submitted' },
  reviewed: { className: 'bg-mozaic-teal/15 text-mozaic-teal', label: 'Reviewed' },
  flagged: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Flagged' },
  amended: { className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amended' },
};

const RISK_BADGE: Record<string, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

function useProjects() {
  const tenantId = useAuthStore((s) => s.tenantId);

  return useQuery({
    queryKey: ['projects', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('is_deleted', false)
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!tenantId,
  });
}

function useDSRReports(filters: Filters, page: number) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const userRole = useAuthStore((s) => s.userRole);
  const projectId = useAuthStore((s) => s.projectId);

  return useQuery({
    queryKey: ['dsr_reports', filters, page, tenantId, userRole, projectId],
    queryFn: async () => {
      // SWA-severity filter requires an inner join on dsr_swa_events so
      // the count and rows reflect DSRs that have at least one event at
      // the requested severity. Without the filter we keep the existing
      // left/no-join shape so DSRs with no SWA events still appear.
      const swaJoin =
        filters.swaSeverity === 'all'
          ? ''
          : ', dsr_swa_events!inner ( severity )';

      let query = supabase
        .from('dsr_reports')
        .select(
          `
          id,
          report_date,
          shift_type,
          status,
          submitted_at,
          created_by,
          project_id,
          overall_risk_level,
          risk_score,
          projects!inner ( name ),
          users!dsr_reports_created_by_fkey ( first_name, last_name, email ),
          dsr_activities_summaries ( overall_risk_level )${swaJoin}
        `,
          { count: 'exact' }
        )
        .eq('is_deleted', false)
        .gte('report_date', filters.dateFrom)
        .lte('report_date', filters.dateTo)
        .order('report_date', { ascending: false })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      // SSRs are always pinned to their assigned project, regardless of
      // dropdown state — the dropdown is hidden in the UI, but enforce
      // here too as a defence-in-depth check.
      if (userRole === 'ssr' && projectId) {
        query = query.eq('project_id', projectId);
      } else if (filters.projectId !== 'all') {
        query = query.eq('project_id', filters.projectId);
      }
      if (filters.risk !== 'all') {
        query = query.eq('overall_risk_level', filters.risk);
      }
      if (filters.swaSeverity !== 'all') {
        query = query
          .eq('dsr_swa_events.severity', filters.swaSeverity)
          .eq('dsr_swa_events.is_deleted', false);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: data ?? [], total: count ?? 0 };
    },
    enabled: !!tenantId,
  });
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.userRole);
  const projectId = useAuthStore((s) => s.projectId);
  const isSSR = userRole === 'ssr';
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<Filters>(() =>
    applyUrlOverrides(defaultFilters(isSSR ? projectId : null), searchParams),
  );
  const [page, setPage] = useState(0);

  // When a dashboard drill-down navigates here mid-session, reapply the
  // URL params on top of whatever filters were last set. SSRs ignore the
  // ?project= override; their project is pinned by the auth store.
  const paramKey = useMemo(
    () =>
      `${searchParams.get('project') ?? ''}|${searchParams.get('risk') ?? ''}|${searchParams.get('status') ?? ''}|${searchParams.get('swa_severity') ?? ''}`,
    [searchParams],
  );
  useEffect(() => {
    if (!paramKey || paramKey === '|||') return;
    setFilters((prev) => applyUrlOverrides(prev, searchParams));
    setPage(0);
  }, [paramKey, searchParams]);

  const { data: projects } = useProjects();
  const { data, isLoading, isError, refetch } = useDSRReports(filters, page);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const updateFilter = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters(defaultFilters(isSSR ? projectId : null));
    setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          {isSSR ? 'My Project Reports' : 'DSR Reports'}
        </h1>
        {isSSR && (
          <Button
            size="sm"
            className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={() => navigate('/dsr/new')}
          >
            New Report
          </Button>
        )}
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 pt-4">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              className="w-36"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              className="w-36"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => updateFilter('status', v ?? 'all')}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="flagged">Flagged</SelectItem>
                <SelectItem value="amended">Amended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!isSSR && (
            <div className="space-y-1">
              <Label className="text-xs">Project</Label>
              <Select value={filters.projectId} onValueChange={(v) => updateFilter('projectId', v ?? 'all')}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Risk Level</Label>
            <Select value={filters.risk} onValueChange={(v) => updateFilter('risk', (v ?? 'all') as Filters['risk'])}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">SWA Severity</Label>
            <Select
              value={filters.swaSeverity}
              onValueChange={(v) =>
                updateFilter('swaSeverity', (v ?? 'all') as Filters['swaSeverity'])
              }
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="minor">Minor</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="serious">Serious</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Report Date</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead>Risk Level</TableHead>
                <TableHead>Submitted At</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {isError && (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center">
                    <p className="text-sm text-muted-foreground">Could not load reports. Try refreshing.</p>
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                    No reports found for the selected filters.
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                rows.map((row: any) => {
                  const project = row.projects as any;
                  const user = row.users as any;
                  const summaries = row.dsr_activities_summaries as any[];
                  // Composite risk first, legacy activities-summary fallback.
                  const riskLevel =
                    row.overall_risk_level ??
                    summaries?.[0]?.overall_risk_level ??
                    null;
                  const statusBadge = STATUS_BADGE[row.status] ?? STATUS_BADGE.draft;
                  const riskBadge = riskLevel ? RISK_BADGE[riskLevel] : null;

                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{project?.name ?? '—'}</TableCell>
                      <TableCell>{format(new Date(row.report_date), 'MMM d, yyyy')}</TableCell>
                      <TableCell className="capitalize">{row.shift_type}</TableCell>
                      <TableCell>
                        <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {user ? `${user.first_name} ${user.last_name}` : '—'}
                      </TableCell>
                      <TableCell>
                        {riskBadge ? (
                          <div className="flex items-center gap-1.5">
                            <Badge className={riskBadge.className}>{riskBadge.label}</Badge>
                            {typeof row.risk_score === 'number' && (
                              <span className="text-xs text-muted-foreground">
                                {row.risk_score}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.submitted_at
                          ? format(new Date(row.submitted_at), 'MMM d, HH:mm')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/reports/${row.id}`)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <span className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages} ({total} reports)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
